<template>
  <div class="toast-overlay" @click.self="handleDismiss">
    <div class="error-toast">
      <div class="toast-header">
        <AlertCircle :size="20" class="icon error-icon" />
        <span class="title">{{ title }}</span>
        <button
          class="close-btn"
          aria-label="Dismiss"
          @click="handleDismiss"
          v-if="dismissible && !isRetrying"
        >
          <X :size="16" />
        </button>
      </div>

      <div class="toast-body">
        <p class="error-message">{{ message }}</p>
        <p v-if="details" class="error-details">{{ details }}</p>
      </div>

      <div class="toast-actions">
        <div class="retry-section">
          <button
            v-if="!isRetrying"
            class="btn btn-primary"
            @click="handleRetryNow"
            :disabled="isWaiting"
          >
            Retry now
          </button>
          <div v-else class="retry-countdown">
            <span class="countdown-text">Retrying in {{ countdownSeconds }}s</span>
            <button
              class="btn btn-secondary"
              @click="handleCancel"
              aria-label="Cancel retry"
            >
              Cancel
            </button>
          </div>
        </div>

        <button
          class="btn btn-secondary"
          @click="handleChangeHost"
          aria-label="Change host"
        >
          Change host
        </button>
      </div>

      <div v-if="showAttemptInfo" class="attempt-info">
        Attempt {{ currentAttempt }} of {{ maxAttempts }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, withDefaults, defineProps, defineEmits } from "vue";
import { useRouter } from "vue-router";
import { AlertCircle, X } from "lucide-vue-next";
import type { RetryPolicy } from "@/utils/retry";

interface Props {
  title?: string;
  message: string;
  details?: string;
  retryPolicy: RetryPolicy;
  dismissible?: boolean;
  showAttemptInfo?: boolean;
  onRetry: () => Promise<void>;
}

interface Emits {
  (e: "dismissed"): void;
  (e: "changeHost"): void;
  (e: "retryStarted"): void;
  (e: "retryCancelled"): void;
}

const props = withDefaults(defineProps<Props>(), {
  title: "Connection Error",
  dismissible: true,
  showAttemptInfo: true,
});

const emit = defineEmits<Emits>();
const router = useRouter();

const isRetrying = ref(false);
const isWaiting = ref(false);
const countdownSeconds = ref(0);
let countdownInterval: ReturnType<typeof setInterval> | null = null;

const currentAttempt = computed(() => props.retryPolicy.getAttemptNumber());
const maxAttempts = computed(() => props.retryPolicy.getMaxAttempts());

onMounted(() => {
  // Start with first retry attempt
  startAutoRetry();
});

onUnmounted(() => {
  if (countdownInterval) {
    clearInterval(countdownInterval);
  }
});

async function startAutoRetry(): Promise<void> {
  if (!props.retryPolicy.shouldRetry()) {
    return;
  }

  isRetrying.value = true;
  isWaiting.value = true;
  emit("retryStarted");

  try {
    const delay = props.retryPolicy.getNextDelay();
    countdownSeconds.value = Math.ceil(delay / 1000);

    countdownInterval = setInterval(() => {
      countdownSeconds.value--;
    }, 1000);

    await props.retryPolicy.waitForNextRetry();

    if (!props.retryPolicy.wasCancelled()) {
      isWaiting.value = false;
      clearInterval(countdownInterval!);
      countdownInterval = null;

      props.retryPolicy.incrementAttempt();
      await performRetry();
    }
  } catch (error) {
    // Retry was cancelled
    if (countdownInterval) {
      clearInterval(countdownInterval);
      countdownInterval = null;
    }
    isRetrying.value = false;
    isWaiting.value = false;
  }
}

async function performRetry(): Promise<void> {
  try {
    await props.onRetry();
    // Success - toast will be dismissed by parent
  } catch (error) {
    // Retry failed, try again if we have attempts left
    if (props.retryPolicy.shouldRetry()) {
      await startAutoRetry();
    } else {
      isRetrying.value = false;
      // Max attempts reached
    }
  }
}

async function handleRetryNow(): Promise<void> {
  isWaiting.value = true;
  try {
    props.retryPolicy.incrementAttempt();
    await performRetry();
  } catch (error) {
    // Error handled by performRetry
  }
}

function handleCancel(): void {
  props.retryPolicy.cancel();
  props.retryPolicy.cancelWait();
  if (countdownInterval) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }
  isRetrying.value = false;
  isWaiting.value = false;
  emit("retryCancelled");
}

function handleChangeHost(): void {
  props.retryPolicy.cancel();
  router.push("/login");
  emit("changeHost");
}

function handleDismiss(): void {
  props.retryPolicy.cancel();
  emit("dismissed");
}
</script>

<style scoped lang="scss">
.toast-overlay {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  top: 0;
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  padding: 16px;
  background: rgba(0, 0, 0, 0.3);
  z-index: 1500;
}

.error-toast {
  background: var(--bg-surface);
  color: var(--text-primary);
  border: 2px solid var(--danger, #e55);
  border-radius: var(--radius-md);
  padding: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  max-width: 500px;
  animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.toast-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-weight: 600;
  font-size: 1rem;

  .icon {
    flex-shrink: 0;
    color: var(--danger, #e55);
  }

  .title {
    flex-grow: 1;
  }

  .close-btn {
    background: none;
    border: none;
    color: var(--text-primary);
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    opacity: 0.6;

    &:hover {
      opacity: 1;
      color: var(--accent);
    }
  }
}

.toast-body {
  margin-bottom: 16px;

  .error-message {
    margin: 0 0 8px 0;
    font-size: 0.95rem;
    line-height: 1.5;
  }

  .error-details {
    margin: 0;
    font-size: 0.85rem;
    color: var(--text-secondary, var(--text-primary));
    opacity: 0.8;
    font-family: monospace;
    word-break: break-word;
  }
}

.toast-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;

  .retry-section {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .retry-countdown {
    display: flex;
    gap: 8px;
    align-items: center;

    .countdown-text {
      font-weight: 600;
      min-width: 120px;
      text-align: right;
      color: var(--text-secondary, var(--text-primary));
    }
  }
}

.btn {
  padding: 6px 16px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: var(--bg-elevated);
  color: var(--text-primary);
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    border-color: var(--accent);
    color: var(--accent);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.btn-primary {
    background: var(--accent);
    color: white;
    border-color: var(--accent);

    &:hover:not(:disabled) {
      background: var(--accent-hover);
      border-color: var(--accent-hover);
      color: white;
    }
  }

  &.btn-secondary {
    background: var(--bg-elevated);
    color: var(--text-primary);

    &:hover:not(:disabled) {
      background: var(--bg-surface);
      border-color: var(--accent);
      color: var(--accent);
    }
  }
}

.attempt-info {
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid var(--border);
  font-size: 0.75rem;
  color: var(--text-secondary, var(--text-primary));
  text-align: right;
}

@media (max-width: 480px) {
  .toast-overlay {
    padding: 8px;
    align-items: flex-end;
  }

  .error-toast {
    max-width: 100%;
    width: 100%;
  }

  .toast-actions {
    flex-direction: column;

    .btn {
      width: 100%;
    }
  }
}
</style>
